import { useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import type { IFilterManagerModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filters-manager/types";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { selectHumanMessagesCount } from "app/pages/sales-intelligence/modules/ai-agent/store";
import {
    StyledSaveAsNewSearchButton,
    StyledSavedSearchButton,
    StyledUpdateSearchButton,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/containers/SavedSearchActionsContainer/styles";
import { useSavedSearchActions } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/hooks/useSavedSearchActions";
import { useSavedSearchViewModel } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/hooks/useSavedSearchViewModel";
import { useUpdateSavedSearchMutationState } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/mutations/useUpdateSavedSearchMutationState";
import { savedSearchFiltersQueryKeys } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/query";
import type { SavedSearchRoutingParams } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/types";
import { ESavedSearchActions } from "app/pages/sales-intelligence/modules/leads-search/sub-modules/saved-search/types/enums";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";
import type { SavedSearchDto } from "app/pages/sales-intelligence/sub-modules/advanced-search/types/dto";

type Props = {
    filtersManager: IFilterManagerModel<LeadsSearchAllFiltersTypes>;
};

const SavedSearchActionsContainer = ({ filtersManager }: Props) => {
    const queryClient = useQueryClient();

    const {
        params: { savedSearchId },
    } = useCurrentStateAndParams<SavedSearchRoutingParams>();

    const humanMessagesCount = useSelector(selectHumanMessagesCount);

    const { onSaveSearch } = useSavedSearchActions(savedSearchId ?? "");

    const updateSavedSearchMutationState = useUpdateSavedSearchMutationState();
    const isUpdateSaveSearchPending = updateSavedSearchMutationState?.status === "pending";

    const { dirtyFilterKeys, areFiltersDirty } = useSavedSearchViewModel({ filtersManager });

    const handleOnSaveSearchTracked = (action: ESavedSearchActions): void => {
        const savedSearchData = queryClient.getQueryData<SavedSearchDto>(
            savedSearchFiltersQueryKeys.getById(savedSearchId),
        );

        if (action === ESavedSearchActions.Save) {
            AIAgentTrackingService.leadGenerator.trackSaveAsNewSearchClick(dirtyFilterKeys, humanMessagesCount);
        } else {
            AIAgentTrackingService.leadGenerator.trackUpdateSearchClick(dirtyFilterKeys, humanMessagesCount);
        }

        onSaveSearch(action, savedSearchData);
    };

    const handleSaveAsNewTracked = () => {
        AIAgentTrackingService.leadGenerator.trackSaveSearchClick(dirtyFilterKeys, humanMessagesCount);

        onSaveSearch(ESavedSearchActions.Save);
    };

    return savedSearchId ? (
        <>
            <StyledUpdateSearchButton
                dataAutomation="agent.update-saved-search-button"
                isDisabled={isUpdateSaveSearchPending || !areFiltersDirty}
                isLoading={isUpdateSaveSearchPending}
                onClick={() => handleOnSaveSearchTracked(ESavedSearchActions.Update)}
            />
            <StyledSaveAsNewSearchButton
                dataAutomation="agent.save-as-new-search-button"
                isDisabled={!areFiltersDirty}
                onClick={() => handleOnSaveSearchTracked(ESavedSearchActions.Save)}
            />
        </>
    ) : (
        <StyledSavedSearchButton
            type="flat"
            dataAutomation="agent.save-search-button"
            isDisabled={!areFiltersDirty}
            onClick={handleSaveAsNewTracked}
        />
    );
};

export default SavedSearchActionsContainer;
